import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8809DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8809 Due Dates for Information Return | TaxZerone®"
          description="File IRS Form 8809 to get an automatic 30-day extension. Request a second extension by the first deadline to avoid IRS penalties on information returns."
          keywords=""
          canonical="resources/tax-extension/form-8809-due-dates/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 8809</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Extend your information return deadline for 30 days using Form 8809
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809duedates`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-8809-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8809 Due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-8809-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 8809
                        </a>{' '}
                        is used by information return filers to request an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic 30-day extension
                        </span>{' '}
                        to file eligible information returns with the IRS
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          Form 8809 must generally be filed by the original due
                          date
                        </span>{' '}
                        of the applicable information return. The deadline
                        varies depending on the type of information return and
                        whether it is filed electronically or on paper.
                      </span>
                    </li>
                    <li>
                      <span>
                        <a
                          href="https://www.taxzerone.com/resources/information-returns/form-w-2-instructions/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Forms W-2
                        </a>{' '}
                        and{' '}
                        <a
                          href="https://www.taxzerone.com/resources/information-returns/form-1099-nec-instructions/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          1099-NEC
                        </a>{' '}
                        generally require{' '}
                        <a
                        // href="https://www.taxzerone.com/tax-extension/form-8809-instructions/"
                        // style={{
                        //   color: '#0202ff',
                        //   textDecoration: 'underline',
                        // }}
                        >
                          Form 8809
                        </a>{' '}
                        to be{' '}
                        <span style={{ fontWeight: 600 }}>filed on paper</span>,
                        while many other eligible information returns can be
                        extended electronically.
                      </span>
                    </li>
                    <li>
                      <span>
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-8809-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Filing Form 8809
                        </a>{' '}
                        on time can help information return filers obtain
                        additional time and{' '}
                        <span style={{ fontWeight: 600 }}>
                          reduce the risk of late-filing penalties
                        </span>
                        .
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      <Link
                        href="/resources/tax-extension/form-8809-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 8809
                      </Link>
                      , is an IRS extension form used by information return
                      filers to get automatic{' '}
                      <Hidden smDown>
                        <br />
                      </Hidden>
                      30-days extension from the filing deadline. It is
                      important to file form 8809 before the deadline of the
                      information return forms you are filing to avoid penalties
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Ways of Filing Form 8809
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Some information return require extension to be filed using
                  paper filing method like{' '}
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form W-2
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1099-nec-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-NEC
                  </Link>{' '}
                  1099-QA,{' '}
                  <Link
                    href="/resources/information-returns/form-5498-qa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-QA
                  </Link>
                  . Apart from these forms if any information return filer
                  require extension for second time need to use paper filing
                  method to request extension of deadline.
                </Typography>
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                    Mailing Address:
                  </Typography>
                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Department of the Treasury
                      </span>
                      <br />
                      Internal Revenue Service Center
                      <br />
                      Ogden, UT 84201-0209
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  Electronic Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS offers a convenient way to file extension forms for
                  information returns online. This method is the quickest and
                  easiest option for getting an extension. While there are some
                  forms that still need to be submitted through paper filing,
                  the IRS highly recommends using electronic filing for eligible
                  forms to extend deadlines more efficiently.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mt: 3, mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 0, fontSize: 15 }}
                      >
                        TaxZerone provide Simple, Secure and affordable e-filing
                        platform
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809duedates`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Deadline for 8809
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The deadline for submitting Form 8809 changes according to the
                  specific forms that are being filed and the method chosen for
                  filing the returns. If any deadline falls on a weekend or
                  holiday, the IRS will consider the next working day as the
                  final date for filing.
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Paper Filing
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Electronic Filing
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            W-2*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            W-2G
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1042-S
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1094-C
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1095
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1097, 1098, 1099
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-QA*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-NEC*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            3921, 3922
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498-QA*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            8027
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Last day of February
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  * Request for extension of Forms 1099-NEC, 1099-QA, 5498-QA,
                  and W-2 must be filed on paper.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Stack spacing={2} direction="row" alignItems="center">
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '24px !important',
                        }}
                      >
                        Don&apos;t Wait till the last date to file Form 8809
                      </Typography>
                      <Typography variant="body2">
                        Get a 30-day extension easily to file your information
                        return
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 120,
                        textAlign: 'center',
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809duedates`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for late filing of Information return
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Days Late
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Penalty for Small Business
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Penalty for Large Business
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1-30 Days Late
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $60 per return (max $239,000 per year)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $60 per return (max $683,000 per year)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            After 30 Days - Aug 1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $130 per return (max $683,000 per year)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $130 per return (max $2,049,000 per year)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            After Aug 1 or Not Filed
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $340 per return (max $1,366,000 per year)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $340 per return (max $4,098,500 per year)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Intentional Disregard
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $680 per return (no maximum penalty)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            $680 per return (no maximum penalty)
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 8809 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxzerone is an IRS authorized e-file service provider,
                  offering the simplest way of e-filing Form 8809
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Step 1:</span> Select the
                  tax year and enter payer/filer information such as name,
                  address, and TIN.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Step 2:</span> Select the
                  form and reason for applying an extension
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Step 3:</span> Review and
                  transmit your return to the IRS and will receive a
                  notification once it is accepted.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        Internal Revenue Service (IRS). <br />
                        For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f8809.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        instruction for Form 8809 and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General Instructions of Information Returns
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Get Extension of Form 8809 easily with <br />
              TaxZerone at just $4.99/return.
            </Typography>

            {/* <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 8809 online?
            </Typography> */}
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809duedates`}
              >
                E-file Form 8809 Now
              </Button>
              <Typography variant="body2">
                Get an extension of 30 days{' '}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8809DueDates;
